<template>
  <div class="grow center wrap">
    <div class="container center col white">
      <div class="d-flex justify-start mb-15">
        <s-btn class="outline" @click="goBack">
          <s-icon class="mr-5">arrow_back</s-icon>
          <span>Back to Login</span>
        </s-btn>
      </div>
      
      <Logo />
      
      <div class="missingText red--text">
        <div v-if="message" class="text-center">
          {{ message }}
        </div>
      </div>
      
      <div class="col gap-20 mt-20">
        <div class="text-center">
          <h2 class="font-24 weight-600 mb-10">Two-Factor Authentication Required</h2>
          <p class="grey--text font-16">For security reasons, you must set up two-factor authentication before accessing your account.</p>
        </div>

        <!-- Step 1: QR Code Display -->
        <div v-if="page === 1" class="col gap-10">
          <div class="text-center">
            <span class="font-16 weight-500">Scan this QR code with your authenticator app</span>
            <span class="weight-300 font-14 grey--text">
              We recommend using 
              <span class="weight-600">Google Authenticator</span> or similar apps
            </span>
          </div>
          <div class="center mt-20">
            <img v-if="qrImage" :src="qrImage" alt="2FA QR Code" class="qr-image" />
            <div v-else class="center pa-40">
              <VueSpinner size="40" color="#1976d2" />
            </div>
          </div>
          <div class="row justify-center">
            <s-btn class="primary white--text" @click="page = 2" :disabled="!qrImage">
              Next Step
            </s-btn>
          </div>
        </div>

        <!-- Step 2: Code Verification -->
        <div v-if="page === 2" class="col gap-10">
          <div class="text-center">
            <span class="font-16 weight-500">Enter the 6-digit code from your authenticator app</span>
            <span class="weight-300 font-14 grey--text">
              This verifies that you've set up 2FA correctly
            </span>
          </div>
          <div class="center mt-20 red--text font-14">
            {{ message }}
          </div>
          <div class="row center pb-20 black--text">
            <v-otp-input 
              ref="otpInput" 
              input-classes="otp-input black--text" 
              separator="-" 
              :num-inputs="6" 
              :should-auto-focus="true" 
              input-type="number" 
              :conditionalClass="['one', 'two', 'three', 'four', 'five', 'six']" 
              :placeholder="['*', '*', '*', '*', '*', '*']" 
              @on-complete="handleOnComplete" 
            />
          </div>
          <div class="row justify-center">
            <s-btn class="primary white--text" @click="confirmOtp" :disabled="code.length !== 6">
              Verify & Complete Setup
            </s-btn>
          </div>
        </div>

        <!-- Step 3: Recovery Code -->
        <div v-if="page === 3" class="col gap-10">
          <div class="text-center">
            <span class="font-16 weight-500">Save your recovery code</span>
            <span class="weight-300 font-14 grey--text">
              Keep this code safe! You'll need it if you lose access to your authenticator app.
            </span>
          </div>
          <div class="row border center w100 pa-20 mt-20">
            <span class="weight-600 font-24 letter-spacing">{{ recoveryCode }}</span>
          </div>
          <div class="row justify-center">
            <s-btn class="primary white--text" @click="completeSetup">
              Complete Setup
            </s-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { push } from '@/assets/js/util'
import Logo from '@/components/Atoms/Logo'
import { VueSpinner } from 'vue3-spinners'
import VOtpInput from 'vue3-otp-input'

const store = useStore()
const router = useRouter()

const page = ref(1)
const message = ref('')
const qrImage = ref('')
const code = ref('')
const recoveryCode = ref('')

const handleOnComplete = (value) => { 
  code.value = value
}

const confirmOtp = async () => {
  if (code.value.length !== 6) {
    message.value = 'Please enter a 6-digit code'
    return
  }
  
  try {
    const res = await store.dispatch('user/finish2fa', code.value)
    if (!res?.recoveryCode) {
      message.value = 'Invalid code. Please try again.'
      return
    }
    
    let parsed = typeof res.recoveryCode === 'string' ? JSON.parse(res.recoveryCode) : res.recoveryCode
    recoveryCode.value = parsed.at(0)
    page.value = 3
  } catch (error) {
    message.value = 'Setup failed. Please try again.'
    console.error('2FA setup error:', error)
  }
}

const completeSetup = async () => {
  try {
    // Refresh user information to get updated 2FA status
    await store.dispatch('user/getUserInformation')
    
    // Navigate to dashboard
    push('/')
  } catch (error) {
    console.error('Error completing 2FA setup:', error)
    message.value = 'Setup completed but there was an issue refreshing your account. Please log out and log back in to continue.'
    
    // Show logout option after a delay
    setTimeout(() => {
      if (confirm('Would you like to log out and log back in to complete the setup?')) {
        store.dispatch('user/logout')
      }
    }, 2000)
  }
}

const goBack = async () => {
  // Logout and redirect to login
  await store.dispatch('user/logout')
  push('/login')
}

const initialize2FA = async () => {
  try {
    const res = await store.dispatch('user/enable2fa')
    if (res?.status !== 'ok') {
      message.value = 'Failed to initialize 2FA setup. Please try again.'
      return
    }
    qrImage.value = res.data
  } catch (error) {
    message.value = 'Failed to initialize 2FA setup. Please try again.'
    console.error('2FA initialization error:', error)
  }
}

onMounted(() => {
  initialize2FA()
})
</script>

<style scoped lang="less">
.container {
  width: 100%;
  max-width: 523px;
  margin: 30px 10px;
  padding: 48px;
  border-radius: 15px;
}

.qr-image {
  max-height: 300px;
  min-height: 300px;
  object-fit: contain;
}

.border {
  border: 1px solid #9f9f9f;
  border-radius: 8px;
}

:deep(.otp-input) {
  width: 40px;
  height: 40px;
  padding: 5px;
  margin: 0 10px;
  font-size: 20px;
  border-radius: 15px;
  text-align: center;
  color: var(--black);
  background-color: var(--shade3);
}
</style>
